import { TableConfig, TableConfigData } from "./TableConfig";
import { DocFieldConfig, DocFieldConfigData } from "./DocFieldConfig";
import { HeliosDocTypeConfig } from "./helios/DocTypeConfig";

export type DocTypeConfigData = {
  typeName: string;
  description: string;
  position: number;
  fields: DocFieldConfigData[];
  tables: TableConfigData[];
  helios?: HeliosDocTypeConfig;
};
export default class DocTypeConfig {
  static UNKNOWN_NAME = "Unknown";
  typeName: string;
  description: string;
  position: number;
  /**
   * Sorted by DocFieldConfig.position.
   */
  fields: DocFieldConfig[];
  tables: TableConfig[];
  unknown: boolean = false;

  helios?: HeliosDocTypeConfig;

  /**
   * Property set through constructor, used to get BatchClassConfig.defaultMatcherThreshold onto each DocFieldConfig.
   * Access through getDefaultMatcherThreshold() to ensure tracibility.
   */
  private bcc_defaultMatcherThreshold: number;

  constructor(data: DocTypeConfigData, defaultMatcherThresold: number) {
    this.typeName = data.typeName;
    this.description = data.description;
    if ((this.description ?? "").length === 0) {
      this.description = this.typeName;
    }
    this.position = data.position;
    this.bcc_defaultMatcherThreshold = defaultMatcherThresold;

    this.fields = data.fields.map((x) => new DocFieldConfig(x));
    this.fields.sort((a, b) => a.position - b.position);

    this.tables = data.tables.map((x) => new TableConfig(x));
    this.unknown = this.typeName === DocTypeConfig.UNKNOWN_NAME;

    this.helios = data.helios;
  }

  /**
   *
   * @param field Object containing fieldName property
   * @param newPosition 0-based index of new field position.
   */
  changeFieldOrder(field: { fieldName: string }, newPosition: number): void {
    if (newPosition > this.fields.length - 1 || newPosition < 0) {
      throw new Error(`Invalid position ${newPosition} provided`);
    }
    const fieldIndex = this.fields.findIndex(
      (x) => x.fieldName === field.fieldName,
    );
    if (fieldIndex === -1) {
      throw new Error(
        `Cannot change order of non-existent field ${field?.fieldName}`,
      );
    }
    // remove field to avoid duplicate array entry
    const [removedField] = this.fields.splice(fieldIndex, 1);
    // insert removed field into new position
    this.fields.splice(newPosition, 0, removedField);
    // update all field positions based on current index.
    this.fields.forEach((f, i) => {
      f.position = i;
      if (f.helios) {
        f.helios.position = i;
      }
    });
  }

  getDefaultMatcherThresold(): number {
    return this.bcc_defaultMatcherThreshold;
  }

  updateDocFieldConfig(docFieldConfig: DocFieldConfig) {
    let index = this.fields.findIndex(
      (x) => x.fieldName === docFieldConfig.fieldName,
    );
    // if(fieldIndex)
    let removeCount: number = 1;
    if (index == -1) {
      // TODO: would allocating to end of the array be faster ?
      // if our object doesn't exist, insert at start of array.
      removeCount = 0;
      index = 0;
    }
    // insert or update the object
    this.fields.splice(index, removeCount, docFieldConfig);
  }

  getDocFieldConfig(fieldName: string): DocFieldConfig | undefined {
    return this.fields.find((field) => field.fieldName === fieldName);
  }

  getTableConfig(tableName: string): TableConfig | undefined {
    return this.tables.find((table) => table.name === tableName);
  }

  /**
   * Marks the field reporting groups this document type's documents by, clearing any other.
   *
   * At most one field can carry each reporting flag - the backend enforces the same rule - because a
   * document has one value to be grouped under and one amount to be summed. Passing a name nothing
   * matches clears the flag from every field, which is how the column unticks.
   */
  markAggregationField(fieldName?: string) {
    this.fields.forEach((field) => {
      field.aggregationField =
        !!fieldName && field.fieldName.toLowerCase() === fieldName.toLowerCase();
    });
  }

  /** Marks the field holding this document type's monetary amount, clearing any other. */
  markValueField(fieldName?: string) {
    this.fields.forEach((field) => {
      field.valueField =
        !!fieldName && field.fieldName.toLowerCase() === fieldName.toLowerCase();
    });
  }

  toData(): DocTypeConfigData {
    return {
      typeName: this.typeName,
      description: this.description,
      position: this.position,
      fields: this.fields.map((x) => x.toData()),
      tables: this.tables.map((x) => x.toData()),
      helios: this.helios ? { ...this.helios } : undefined,
    };
  }

  static getUnknown(): DocTypeConfig {
    const docTypeConfig = new DocTypeConfig(
      {
        typeName: DocTypeConfig.UNKNOWN_NAME,
        description: DocTypeConfig.UNKNOWN_NAME,
        position: 0,
        fields: [],
        tables: [],
      },
      0,
    );
    return docTypeConfig;
  }

  static createTest(
    rest: Partial<DocTypeConfigData> = {},
    defaultMatcherThreshold: number = 0,
  ): DocTypeConfig {
    const data: DocTypeConfigData = {
      typeName: "",
      description: "",
      position: 0,
      fields: [],
      tables: [],
      helios: undefined,
    };

    return new DocTypeConfig(
      {
        ...data,
        ...rest,
      },
      defaultMatcherThreshold,
    );
  }
}
